import React from 'react'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact, { BugsnagErrorBoundary } from '@bugsnag/plugin-react'

// Hardcoded on purpose: this monitor must be active on every environment
// (stage and prod) without any build-time configuration, so field errors on
// the projects screen are never lost to a missing env var.
const BUGSNAG_API_KEY = '9d10d3bacc207969ea2a8800b024c163'

// Track ONLY the projects screen — /projects/:id for every project status
// (draft, assigned, finished) — plus the smoke-check route below it.
const TRACKED_PATH = /^\/projects(\/|$)/

export const currentBundle = (): string => {
  const src = Array.from(document.scripts)
    .map(script => script.src)
    .find(source => /app-[A-Z0-9]+\.js/i.test(source))
  return src?.match(/app-([A-Z0-9]+)\.js/i)?.[1] ?? 'dev'
}

export const currentUserEmail = (): string | undefined => {
  try {
    const raw = window.localStorage.getItem('current_user')
    if (!raw) return undefined
    // Mantine's useLocalStorage stores the JSON payload re-stringified, so the
    // value is double-encoded: parse once, and once more if still a string
    let parsed = JSON.parse(raw)
    if (typeof parsed === 'string') parsed = JSON.parse(parsed)
    return parsed?.email ?? parsed?.username ?? undefined
  } catch {
    return undefined
  }
}

// Derive the stage from the hostname so staging and production events can be
// filtered apart on the dashboard (both are NODE_ENV=production builds)
const releaseStage = (): string => {
  const { hostname } = window.location
  if (hostname === 'app.mosaicoed.com') return 'production'
  if (hostname.startsWith('staging.')) return 'staging'
  return 'development'
}

let started = false

export const startProjectsScreenMonitoring = () => {
  if (started) return

  // The monitor must never take the app down: this runs at boot in module
  // scope, so a throwing Bugsnag.start would otherwise white-screen every
  // route. started only flips after a successful start.
  try {
    Bugsnag.start({
      apiKey: BUGSNAG_API_KEY,
      plugins: [new BugsnagPluginReact()],
      appVersion: currentBundle(),
      releaseStage: releaseStage(),
      // API failures are reported by the axios interceptor already; leaving
      // unhandled rejections on would double-report every orphan api promise
      enabledErrorTypes: { unhandledExceptions: true, unhandledRejections: false },
      onError: event => {
      // Drop everything that did not happen on the projects screen
        if (!TRACKED_PATH.test(window.location.pathname)) return false

        const email = currentUserEmail()
        if (email) event.setUser(undefined, email, undefined)

        event.addMetadata('app_details', {
          bundle: currentBundle(),
          url: window.location.href,
          projectId: window.location.pathname.match(/^\/projects\/([0-9a-f-]{36})/i)?.[1] ?? null,
          browser: window.navigator.userAgent,
        })

        return true
      },
    })
    started = true
  } catch {
    // intentionally silent — the app must boot even if the monitor cannot
  }
}

// React render crashes on the projects screen: the boundary reports the
// component stack to the monitor and keeps the rest of the app alive.
export const getProjectsScreenErrorBoundary = (): BugsnagErrorBoundary | null => {
  if (!started) return null
  return Bugsnag.getPlugin('react')?.createErrorBoundary(React) ?? null
}

export const notifyProjectsScreenError = (
  error: unknown,
  metadata?: Record<string, unknown>,
  onDelivery?: (delivered: boolean) => void,
) => {
  if (!started) return

  const normalized = error instanceof Error ? error : new Error(String(error))

  Bugsnag.notify(
    normalized,
    event => {
      if (metadata) event.addMetadata('request', metadata)
    },
    deliveryError => onDelivery?.(!deliveryError),
  )
}
